import { useSyncExternalStore, type SVGProps } from "react";

import type { Glyph, IconName, Mark } from "./glyphs";
import { ICON_MOTION_CSS } from "./motion";

/** The one stroke weight, in grid units. */
export const ICON_STROKE = 1.75;

/**
 * The box shows the drawing square (2-22 of the 24 grid), not the grid: the 2-unit margin was
 * dead space that made every icon read a fifth smaller than its box. A round cap or a spark that
 * reaches into the margin still draws, past the box's edge (`overflow: visible`). The stroke is
 * scaled with the crop so a line keeps its weight in px.
 */
export const ICON_VIEWBOX = "2 2 20 20";
export const ICON_STROKE_IN_BOX = (ICON_STROKE * 20) / 24;

/**
 * The drawings arrive apart from `Icon`. The first paint draws a handful of icons but every
 * page imports `Icon`, so a static import would put all 215 drawings into the bytes every
 * visitor downloads before anything shows (scripts/chunk-budgets.ts). The console's shell
 * hands the table over with `provideGlyphs` when it loads; anything drawn before that (or
 * anywhere without the shell) fetches the table on first use and keeps its box meanwhile.
 */
export type GlyphTable = Readonly<Record<IconName, Glyph>>;

let glyphTable: GlyphTable | undefined;
let glyphsLoading: Promise<void> | undefined;
const glyphListeners = new Set<() => void>();

export function provideGlyphs(glyphs: GlyphTable): void {
  if (glyphTable) return;
  glyphTable = glyphs;
  for (const listener of glyphListeners) listener();
}

function subscribeGlyphs(listener: () => void): () => void {
  glyphListeners.add(listener);
  glyphsLoading ??= import("./glyphs").then((module) => provideGlyphs(module.GLYPHS));
  return () => glyphListeners.delete(listener);
}

const currentGlyphs = (): GlyphTable | undefined => glyphTable;

/**
 * How the accent part is coloured. `mono`: the icon's colour, always. `accent`: brand orange,
 * always. `contextual`: brand orange where the surface sets `--icon-accent` (an active or
 * selected row does), the icon's colour elsewhere.
 */
export type IconTone = "mono" | "accent" | "contextual";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name" | "children"> {
  readonly name: IconName;
  /** Width and height in px. */
  readonly size?: number;
  /** A name for assistive technology; without it the icon is decoration and hidden from it. */
  readonly title?: string;
  readonly tone?: IconTone;
  /** The thing the icon stands for is happening now (recording, training, working, loading). */
  readonly animate?: boolean;
}

const ACCENT_COLOUR: Readonly<Record<IconTone, string>> = {
  mono: "currentColor",
  accent: "hsl(var(--brand))",
  contextual: "var(--icon-accent, currentColor)",
};

function paintOf(mark: Mark, tone: IconTone): Pick<SVGProps<SVGElement>, "fill" | "stroke"> {
  if (mark.role === "point") return { fill: "currentColor", stroke: "none" };
  if (mark.role !== "accent") return {};
  const colour = ACCENT_COLOUR[tone];
  return mark.solid ? { fill: colour, stroke: colour } : { stroke: colour };
}

function MarkShape({ mark, tone }: { readonly mark: Mark; readonly tone: IconTone }) {
  const shared = {
    ...paintOf(mark, tone),
    "data-sx-motion": mark.motion,
    pathLength: mark.motion === "draw" ? 1 : undefined,
  };
  switch (mark.kind) {
    case "path":
      return <path d={mark.d} {...shared} />;
    case "circle":
      return <circle cx={mark.cx} cy={mark.cy} r={mark.r} {...shared} />;
    case "rect":
      return <rect x={mark.x} y={mark.y} width={mark.width} height={mark.height} rx={mark.rx} {...shared} />;
  }
}

/** One icon from the sentientx set. Colour is `currentColor`; size is the box in px. */
export function Icon({ name, size = 16, title, tone = "contextual", animate = false, ...svg }: IconProps) {
  const glyph = useSyncExternalStore(subscribeGlyphs, currentGlyphs, currentGlyphs)?.[name];
  const motion = glyph?.motion;
  return (
    <>
      {motion ? (
        <style href="sx-icon-motion" precedence="sx-icon">
          {ICON_MOTION_CSS}
        </style>
      ) : null}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={ICON_VIEWBOX}
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth={ICON_STROKE_IN_BOX}
        overflow="visible"
        strokeLinecap="round"
        strokeLinejoin="round"
        role={title ? "img" : undefined}
        aria-hidden={title ? undefined : true}
        focusable="false"
        data-icon={name}
        data-sx-animate={motion && animate ? "" : undefined}
        data-sx-hover={motion?.trigger === "hover" ? "" : undefined}
        {...svg}
      >
        {title ? <title>{title}</title> : null}
        {glyph?.marks.map((mark, index) => (
          <MarkShape key={index} mark={mark} tone={tone} />
        ))}
      </svg>
    </>
  );
}
